import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../../firebase";
import { useActiveBar } from "../ActiveBarContext";
import "./OwnerShell.css";

// The owner dashboard is grouped into 5 sections. Each section can have
// sub-tabs (the existing pages), shown as pills at the top of the page.
// All the old page addresses still work; they just live inside a section now.
const ADVERTISE_URL = "https://barfunfinder.com/advertise.html";

const SECTIONS = [
  {
    label: "Today",
    to: "/owner",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.5 1.5M17.5 17.5L19 19M5 19l1.5-1.5M17.5 6.5L19 5" />
      </svg>
    ),
    tabs: [],
  },
  {
    label: "Schedule",
    to: "/owner/create-event",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <rect x="3" y="5" width="18" height="16" rx="2" />
        <path d="M3 10h18M8 3v4M16 3v4" />
      </svg>
    ),
    tabs: [
      { label: "Events", to: "/owner/create-event" },
      { label: "Specials", to: "/owner/specials" },
      { label: "Today's food photo", to: "/owner/todays-special" },
    ],
  },
  {
    label: "My Bar",
    to: "/owner/bar-profile",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M3 21V10l9-6 9 6v11" />
        <path d="M9 21v-6h6v6" />
      </svg>
    ),
    tabs: [
      { label: "Profile & photos", to: "/owner/bar-profile" },
      { label: "Jobs", to: "/owner/jobs" },
      { label: "Charity gaming", to: "/owner/gaming-inventory" },
      { label: "Linked charities", to: "/owner/linked-venues" },
      { label: "Claim another bar", to: "/owner/claim-bar" },
    ],
  },
  {
    label: "Promote",
    to: "/owner/subscription",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M3 11l14-6v14L3 13z" />
        <path d="M7 13v5h3" />
      </svg>
    ),
    tabs: [
      { label: "Get featured", to: "/owner/subscription" },
      { label: "Offers", to: "/owner/offers" },
      { label: "Send an update", to: "/owner/post-update" },
      { label: "Advertise", href: ADVERTISE_URL },
    ],
  },
  {
    label: "Account & Help",
    to: "/owner/account",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21a8 8 0 0 1 16 0" />
      </svg>
    ),
    tabs: [
      { label: "Account", to: "/owner/account" },
      { label: "Help", to: "/owner/help" },
    ],
  },
];

function cleanPath(path) {
  return path.length > 1 ? path.replace(/\/+$/, "") : path;
}

function sectionFor(pathname) {
  const path = cleanPath(pathname);
  return (
    SECTIONS.find((s) => s.tabs.some((t) => t.to && cleanPath(t.to) === path)) ||
    SECTIONS[0]
  );
}

export default function OwnerLayout({ children }) {
  const { myBars, activeBarId, setActiveBarId } = useActiveBar();
  const navigate = useNavigate();
  const location = useLocation();
  const current = sectionFor(location.pathname);
  const path = cleanPath(location.pathname);

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/login");
  };

  return (
    <div className="os-shell">
      <aside className="os-side">
        <p className="os-brand"><img src="/logo-header.png" alt="Bar Fun Finder" style={{ width: "170px", maxWidth: "100%", height: "auto", display: "block" }} /></p>

        {myBars.length > 0 && (
          <>
            <label htmlFor="os-bar" className="os-bar-label">Your bar</label>
            <select
              id="os-bar"
              className="os-bar-select"
              value={activeBarId}
              onChange={(e) => setActiveBarId(e.target.value)}
            >
              {myBars.map((bar) => (
                <option key={bar.id} value={bar.id}>{bar.name || "Unnamed Bar"}</option>
              ))}
            </select>
          </>
        )}

        <nav className="os-nav" aria-label="Dashboard">
          {SECTIONS.map((s) => (
            <NavLink
              key={s.label}
              to={s.to}
              className={`os-nav-link ${current.label === s.label ? "active" : ""}`}
              aria-current={current.label === s.label ? "page" : undefined}
            >
              {s.icon}
              {s.label}
            </NavLink>
          ))}
        </nav>

        <button type="button" className="os-logout" onClick={handleLogout}>Log out</button>
      </aside>

      <main className="os-main">
        {current.tabs.length > 0 && (
          <nav className="os-subtabs" aria-label={`${current.label} pages`}>
            {current.tabs.map((t) =>
              t.href ? (
                <a key={t.label} className="os-subtab" href={t.href} target="_blank" rel="noopener noreferrer">
                  {t.label} ↗
                </a>
              ) : (
                <NavLink
                  key={t.label}
                  to={t.to}
                  end
                  className={`os-subtab ${cleanPath(t.to) === path ? "active" : ""}`}
                >
                  {t.label}
                </NavLink>
              )
            )}
          </nav>
        )}
        {children}
      </main>
    </div>
  );
}
